// --- Our Modules --------------------------------------------------------------------------------
import type { Translation } from "./types";
// --- Logic --------------------------------------------------------------------------------------
const en: Translation = {
    common: {
        loading: "Loading…",
        retry: "Try again",
        somethingWrong: "Something went wrong, try again",
        logout: "Sign out",
        save: "Save",
        cancel: "Cancel",
        more: "More",
        close: "Close",
        confirm: "Confirm",
        done: "Done",
        copy: "Copy",
        copied: "Copied",
        typeToConfirm: "Type “{word}” to confirm",
        filters: "Filters",
        search: "Search",
        applyFilters: "Apply",
        clearFilters: "Clear filters",
        back: "Back",
        edit: "Edit",
        remove: "Delete",
        add: "Add",
        notSet: "Not set",
        yes: "Yes",
        no: "No",
        all: "All",
        pagination: "Pagination",
        previousPage: "Previous page",
        nextPage: "Next page",
        pageCount: "{count} of {total}",
        perPage: "per page",
        comingSoon: "Being built",
    },
    auth: {
        title: "Admins panel",
        subtitle: "Lozicore's own team only",
        phone: "Phone number",
        phonePlaceholder: "5xxxxxxxx",
        password: "Password",
        passwordPlaceholder: "••••••••",
        login: "Sign in",
        phoneRequired: "Enter a valid phone number",
        passwordRequired: "Enter your password",
        temporaryPassword: "Your password is a temporary one — change it from your account page.",
    },
    sideBar: {
        brand: "Lozicore",
        kind: "Admins panel",
        home: "Home",
        organizations: "Companies",
        subscriptions: "Subscriptions",
        clients: "People",
        enquiries: "Enquiries",
        support: "Support",
        admins: "Admins",
    },
    /** Account requests arriving from the marketing site's form. */
    enquiries: {
        title: "Enquiries",
        subtitle: "{new} new · {total} in all",
        tabs: { all: "All", new: "New" },
        statuses: { new: "New", read: "Read", done: "Done" },
        empty: "Nothing here.",
        searchHint: "A company, a person or a number",
        detailsTitle: "Enquiry details",
        moveStatus: "Move the status",
        confirmStatus: "Move this enquiry to ({status})?",
        noMessage: "They left no message.",
        convert: "Create a company from this",
        convertHint: "Opens the company form filled in with what they wrote, then seats its first owner.",
        call: "Call",
    },
    support: {
        title: "Support",
        subtitle: "{pending} waiting · {total} in all",
        openHint: "Everything nobody has closed out yet. A request waiting on us is the only figure here with a deadline on it.",
        tabs: { open: "Open", pending: "Waiting on us", contacted: "In hand", solved: "Solved", all: "All" },
        statuses: { pending: "Waiting on us", contacted: "In hand", solved: "Solved" },
        statTotal: "All requests",
        answered: "Answered",
        empty: "Nothing here.",
        searchHint: "A subject or a word in the message",
        detailsTitle: "Request details",
        respond: "Answer",
        editReply: "Edit the answer",
        openClient: "Open this person's page",
        moveStatus: "Move it",
        confirmStatus: "Move this request to ({status})?",
        fields: { reply: "Your answer" },
        replyHint: "They read this in their own dashboard. There is no email or SMS, so what you write here is the whole answer.",
        unknownCompany: "Unknown company",
        unknownPerson: "Unknown person",
    },
    screens: {
        home: "Home",
        organizations: "Companies",
        organization: "Company",
        subscriptions: "Subscriptions",
        clients: "People",
        client: "Person",
        support: "Support",
        admins: "Admins",
        profile: "My account",
        notFound: "Page not found",
    },
    home: {
        greetingMorning: "Good morning",
        greetingAfternoon: "Good afternoon",
        greetingEvening: "Good evening",
        subtitle: "The whole system on one screen",
        attentionTitle: "Needs action",
        attentionClear: "Nothing is waiting on you today",
        attentionPayments: "Transfers to confirm",
        attentionGrace: "In the grace window",
        attentionEnding: "Ending soon",
        attentionStorage: "Storage nearly full",
        attentionStorageRequests: "Storage requests",
        attentionTickets: "Support requests",
        organizations: "Companies",
        activeSubscriptions: "Active subscriptions",
        clients: "People",
        storageUsed: "Storage used",
        newest: "Newest companies",
        endingSoon: "Ending soon",
        seeAll: "See all",
    },
    organizations: {
        title: "Companies",
        subtitle: "{count} companies",
        searchPlaceholder: "Name, public address or city",
        addOrganization: "New company",
        empty: "No matching companies",
        tabs: { all: "All", blocked: "Blocked" },
        recordTabs: { details: "Details", usage: "Usage", subscription: "Subscription", people: "Team" },
        access: { free: "Free", full: "Active", readOnly: "Read-only", blocked: "Blocked", grace: "Grace" },
        counts: { projects: "Projects", units: "Units", customers: "Customers", clients: "Staff" },
        fields: {
            name: "Company name",
            slug: "Public address",
            city: "City",
            address: "Address",
            phone: "Phone",
            email: "Email",
            crNumber: "CR number",
            theme: "Catalog theme",
            languages: "Published languages",
            createdAt: "Created",
        },
        createTitle: "New company",
        editTitle: "Edit company",
        nameRequired: "The company name is required in at least one language",
        slugHint: "Becomes its public catalog address: {host}",
        slugInvalid: "The address takes lowercase letters, numbers and single hyphens between words, and never starts or ends with one.",
        slugFix: "Fix the public address before saving.",
        storage: "Storage",
        storageOf: "{used} of {limit}",
        openCatalog: "Public catalog",
        block: "Block the company",
        unblock: "Unblock",
        confirmBlock: "“{name}” becomes unreachable for all of its staff and its public catalog closes. Its data is never deleted.",
        confirmUnblock: "The staff of “{name}” go back to work and its public catalog opens again.",
        noSubscription: "This company has never subscribed, and works inside the free quota.",
        freeQuota: "Free quota: {projects} projects · {units} units · {storage}",
        endsIn: "Ends in {days} days",
        lapsed: "Lapsed {days} days ago",
        history: "Past periods",
        noPeople: "Nobody works here yet",
        owner: "Owner",
        setOwner: "Set the owner",
        ownerTitle: "The owner of {name}",
        ownerHint:
            "A company is created empty, and only its owner can add the rest of its staff — so this is the one account Lozicore creates for it. There is exactly one owner, and afterwards it moves only by the owner promoting somebody from their own team screen.",
        ownerNameHint:
            "If the number already belongs to somebody, they take the company on and keep the password they already use — and this name is ignored.",
        peopleHint: "Everyone else is added by the owner, from the company's own team screen.",
    },
    subscriptions: {
        title: "Subscriptions",
        subtitle: "{pending} awaiting confirmation · {total} periods",
        searchPlaceholder: "Company name or public address",
        empty: "Nothing here",
        emptyPending: "No transfer is waiting to be confirmed",
        emptyEnding: "Every live period has more than {days} days left",
        tabs: { pending: "Awaiting transfer", storage: "Storage requests", live: "Live", ending: "Ending soon", history: "History" },
        periods: { quarterly: "Quarterly", annually: "Annual" },
        statuses: {
            pending: "Awaiting transfer",
            active: "Live",
            coming: "Queued",
            grace: "Grace",
            ended: "Ended",
            cancelled: "Cancelled",
        },
        fields: {
            organization: "Company",
            period: "Period",
            basePrice: "Plan price",
            price: "Amount due",
            amountReceived: "Amount received",
            addons: "Storage addon",
            paymentDueAt: "Transfer deadline",
            startsAt: "Starts",
            endsAt: "Ends",
            paidAt: "Transfer date",
            receipt: "Receipt reference",
            note: "Note",
        },
        currency: "SAR",
        recordSubscription: "Record a subscription",
        createTitle: "Record a subscription",
        renewTitle: "Queue a renewal",
        priceHint: "Leave it empty to record {amount} SAR — the plan price plus the addons — or type what was agreed if there is a discount.",
        addonsHint: "Each addon is {gb} GB at {price} {currency}, full price, and is carried into the next period.",
        confirmPayment: "Confirm payment",
        confirmTitle: "Confirm {name}'s payment",
        confirmHint: "The period starts on the transfer date, or after the current one if the company already subscribes.",
        receiptHint: "The transaction number, or any reference that finds this transfer on a statement.",
        cancelRequest: "Cancel the request",
        confirmCancelRequest: "The request for “{name}” will be cancelled. A new one can be recorded at any time.",
        pendingHint: "A request with no transfer after {days} days is cancelled by the nightly job.",
        addStorage: "Add storage",
        storageTitle: "Add storage for {name}",
        storageHint: "Added to the live period and available at once. It does not change the period's recorded amount.",
        storageCount: "How many addons",
        pastPeriods: "Past periods",
        currentPeriod: "Current period",
        addonOf: "Storage addon ×{count}",
        addonWaiting: "Storage request ×{count}",
        confirmStorage: "Confirm the storage transfer",
        confirmStorageMessage: "Confirm {amount} SAR received from {name} for {count} pack(s) ({gb} GB). The space is added at once.",
        storageRequestHint: "Storage asked for by companies on a running period. The space is not added until the transfer is confirmed.",
        emptyStorage: "No storage requests are waiting for a transfer.",
        requestedBy: "Asked for by {name}",
        dueOn: "Due {date}",
        noOrganizations: "There are no companies yet — create one first.",
    },
    clients: {
        title: "People",
        subtitle: "{count} people · identity is the phone number",
        searchPlaceholder: "Name or phone number",
        empty: "No matching people",
        tabs: { all: "All", owners: "Owners", multi: "In several companies", temporary: "Temporary password", suspended: "Suspended" },
        recordTabs: { memberships: "Companies", details: "Details" },
        memberships: "Companies they work in",
        noMemberships: "Belongs to no company",
        inCompanies: "{count} companies",
        editTitle: "Edit the details",
        nameRequired: "The name is required",
        notesHint: "For whoever comes after you: what this account is, who asked about it.",
        suspend: "Suspend the account",
        activate: "Activate the account",
        confirmSuspend:
            "“{name}” will not be able to sign in to any company, and every device of theirs is signed out. Their records and history are never deleted.",
        confirmActivate: "“{name}” will be able to sign in again, with the same password as before.",
        active: "Active",
        suspended: "Suspended",
        fields: { name: "Name", phone: "Phone", email: "Email", notes: "Notes", createdAt: "Added", status: "Status" },
    },
    admins: {
        title: "Admins",
        subtitle: "Lozicore's own team · {count} admins",
        addAdmin: "New admin",
        empty: "No matching admins",
        superOnly: "Super admins only. Other admins do not see this tab at all.",
        super: "Super admin",
        active: "Active",
        suspended: "Suspended",
        searchPlaceholder: "Name or phone number",
        tabs: { all: "All", supers: "Super admins", suspended: "Suspended" },
        createTitle: "New admin",
        editTitle: "Edit admin",
        fields: { name: "Name", phone: "Phone number", email: "Email", notes: "Notes", isSuper: "Super admin", createdAt: "Added" },
        isSuperHint: "Reaches everything, including managing the admins themselves.",
        notesHint: "For whoever comes after you: what this account is for, and who asked for it.",
        deactivate: "Deactivate",
        activate: "Activate",
        confirmDeactivate:
            "“{name}” will not be able to sign in, and every device of theirs is signed out at once. The account and its history are never deleted.",
        confirmActivate: "“{name}” can sign in again with the password they already have.",
        lastSuperHint: "The last active super admin — cannot be deactivated or demoted.",
        me: "You",
        nameRequired: "The name is required",
        selfSuperHint: "You cannot change your own access — another super admin does that.",
    },
    password: {
        issue: "New password",
        title: "A new password for {name}",
        hint: "Copy it and hand it over now — it is not shown again, and the holder can change it after signing in.",
        confirmHint:
            "A new password will be generated for “{name}”. Their current one stops working at once and every device of theirs is signed out.",
        temporary: "Temporary password",
    },
    slugInfo: {
        title: "What is the public address?",
        definition: "It is the address that opens the company's public catalog on the web.",
        exampleLabel: "Example",
        exampleCaption: "This part is the one you choose",
        changeNote:
            "It can be changed later, but changing it moves the catalog to another address — and every link the company has already shared stops working.",
        rulesTitle: "Rules",
        rules: [
            "Lowercase English letters and numbers only",
            "One hyphen ( - ) between words",
            "Never starts or ends with a hyphen",
            "No spaces, Arabic letters, or symbols like @ # !",
            "No two companies share one address",
        ],
        button: "Got it",
    },
    roles: { owner: "Owner", manager: "Manager", member: "Member" },
    pending: {
        hint: "This section is not built yet. Nothing feeds it, and nothing will be drawn here until something does.",
        waiting: "Waiting on",
        navTitle: "Not built yet",
    },
    profile: {
        myProfile: "My account",
        changePassword: "Change password",
        currentPassword: "Current password",
        newPassword: "New password",
        confirmPassword: "Confirm the new password",
        passwordChanged: "Password changed",
        passwordHint: "Changing it signs out every other device and leaves only this one.",
        passwordMismatch: "The two passwords do not match",
        passwordTooShort: "A password is at least {count} characters",
    },
};

export default en;
